import React, { useEffect, useId, useRef } from 'react';
import ReactDOM from 'react-dom';
import useFocusTrap from '../../../hooks/useFocusTrap';
import './WizardOverlay.css';

/**
 * WizardOverlay — full-viewport dialog shell for the Offer Wizard.
 *
 * Independent of ui/Modal because it needs a 3-row grid (header / body / footer)
 * with a 2-column body (main + summary sidebar) on ≥1024px.
 */
export default function WizardOverlay({
  open,
  onClose,
  title,
  step = 0,
  steps = [],
  children,
  footer,
  summary,
  dirty = false,
  initialFocusRef,
}) {
  const rootRef = useRef(null);
  const titleId = useId();

  const requestClose = () => {
    if (!onClose) return;
    if (dirty) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm('Discard your unsaved offer?');
      if (!ok) return;
    }
    onClose();
  };

  // Escape closes (with dirty confirmation)
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dirty, onClose]);

  // Body scroll lock
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useFocusTrap(rootRef, { active: !!open, initialFocusRef });

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const currentStep = steps[step];
  const stepCountLabel = steps.length > 0
    ? `Step ${step + 1} of ${steps.length}${currentStep?.label ? `: ${currentStep.label}` : ''}`
    : '';

  return ReactDOM.createPortal(
    <div className="wizard-overlay" role="presentation">
      <div
        ref={rootRef}
        className="wizard-overlay__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="wizard-overlay__header">
          <button
            type="button"
            className="wizard-overlay__close"
            onClick={requestClose}
            aria-label="Close wizard"
          >
            ×
          </button>
          <div className="wizard-overlay__heading">
            <h2 id={titleId} className="wizard-overlay__title">{title}</h2>
            {steps.length > 0 && (
              // Announce the step change to screen readers (the visual stepper
              // below is aria-hidden).
              <p className="wizard-overlay__step-count" role="status" aria-live="polite" aria-atomic="true">
                {stepCountLabel}
              </p>
            )}
          </div>
          {steps.length > 0 && (
            <ol className="wizard-overlay__steps" aria-hidden="true">
              {steps.map((s, idx) => {
                const state = idx < step ? 'complete' : idx === step ? 'current' : 'upcoming';
                return (
                  <React.Fragment key={s.key ?? idx}>
                    <li className={`wizard-overlay__step wizard-overlay__step--${state}`}>
                      <span className="wizard-overlay__dot">
                        {state === 'complete' ? '✓' : idx + 1}
                      </span>
                      <span className="wizard-overlay__step-label">{s.label}</span>
                    </li>
                    {idx < steps.length - 1 && (
                      <span
                        className={`wizard-overlay__connector wizard-overlay__connector--${idx < step ? 'complete' : 'upcoming'}`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </ol>
          )}
        </header>

        <div className="wizard-overlay__body">
          <main className="wizard-overlay__main">{children}</main>
          {summary && (
            <aside className="wizard-overlay__summary" aria-label="Offer summary">
              {summary}
            </aside>
          )}
        </div>

        <footer className="wizard-overlay__footer">{footer}</footer>
      </div>
    </div>,
    document.body
  );
}
